import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, UserCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import { useT } from '../i18n';
import { googleLoginRedirect, consumeGoogleSession, hasGoogleSessionHash, GoogleIcon } from '../lib/googleAuth';
import { AuthShell } from '../components/AuthShell';
import { HumanCheck } from '../components/HumanCheck';
import { ClientOnboarding } from '../components/client/ClientOnboarding';
import { ClientDashboard } from '../components/client/ClientDashboard';
import { useOsTheme, ThemeToggle } from '../os3/theme';
import { TextSizePill } from '../os3/OsFooter';

const API = process.env.REACT_APP_BACKEND_URL;

const clientFetch = (path, options = {}) =>
  fetch(`${API}/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

const ERR_MAP = {
  invalid_credentials: ['Email o contraseña incorrectos.', 'Wrong email or password.'],
  account_exists: ['Ya existe una cuenta con este email. Inicia sesión.', 'An account with this email already exists. Please log in.'],
  weak_password: ['La contraseña debe tener al menos 8 caracteres.', 'Password must be at least 8 characters.'],
  invalid_email: ['Email no válido.', 'Invalid email.'],
  too_many_attempts: ['Demasiados intentos. Espera 15 minutos.', 'Too many attempts. Wait 15 minutes.'],
  use_admin_login: ['Esta cuenta es del equipo Kitxens — usa /login.', 'This is a Kitxens team account — use /login.'],
  human_check_failed: ['Verificación caducada — marca de nuevo "No soy un robot".', 'Verification expired — tick "I\'m not a robot" again.'],
};

export default function AccountPage() {
  const navigate = useNavigate();
  const { lang } = useT();
  const isEs = lang === 'es';
  const [theme, setTheme] = useOsTheme();
  const [state, setState] = useState('checking'); // checking | login | onboarding | open
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState('login'); // login | register
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [humanToken, setHumanToken] = useState('');

  const c = isEs ? {
    title: 'Mi Kitxens', google: 'Continuar con Google', or: 'o con tu email',
    tabLogin: 'Entrar', tabRegister: 'Crear cuenta gratis',
    name: 'Tu nombre', email: 'Email', password: 'Contraseña (mín. 8 caracteres)',
    submitLogin: 'Entrar a mi panel', submitRegister: 'Crear mi cuenta gratis',
    loginT: 'Bienvenido a Mi Kitxens', loginSub: 'Tu panel gratuito: noticias de tu zona, clima, oportunidades y tu Index AI Score™.',
    errGoogle: 'No pudimos verificar tu cuenta de Google.', errGeneric: 'Algo falló. Inténtalo de nuevo.',
  } : {
    title: 'My Kitxens', google: 'Continue with Google', or: 'or with your email',
    tabLogin: 'Log in', tabRegister: 'Create free account',
    name: 'Your name', email: 'Email', password: 'Password (min. 8 characters)',
    submitLogin: 'Enter my dashboard', submitRegister: 'Create my free account',
    loginT: 'Welcome to My Kitxens', loginSub: 'Your free dashboard: local news, weather, opportunities and your Index AI Score™.',
    errGoogle: "We couldn't verify your Google account.", errGeneric: 'Something went wrong. Please try again.',
  };

  const routeUser = (u) => {
    setUser(u);
    setState(u?.city ? 'open' : 'onboarding');
  };

  useEffect(() => {
    const loadMe = async () => {
      try {
        const r = await clientFetch('/client/profile');
        if (r.ok) routeUser((await r.json()).user);
        else setState('login');
      } catch { setState('login'); }
    };
    if (hasGoogleSessionHash()) {
      consumeGoogleSession('auto')
        .then((d) => {
          if (d?.context === 'admin') { navigate('/admin/mission-control', { replace: true }); return; }
          if (d?.context === 'partner') { navigate('/affiliates/dashboard', { replace: true }); return; }
          loadMe();
        })
        .catch(() => { setErr(c.errGoogle); setState('login'); });
    } else loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (mode === 'register' && !humanToken) {
      setErr(isEs ? 'Confirma que no eres un robot.' : "Please confirm you're not a robot.");
      return;
    }
    setBusy(true);
    try {
      const path = mode === 'register' ? '/auth/client/register' : '/auth/client/login';
      const body = mode === 'register'
        ? { email: form.email, password: form.password, name: form.name }
        : { email: form.email, password: form.password };
      const r = await clientFetch(path, {
        method: 'POST', body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json', ...(mode === 'register' ? { 'X-Human-Token': humanToken } : {}) },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const code = typeof d.detail === 'string' ? d.detail : '';
        setErr(ERR_MAP[code] ? ERR_MAP[code][isEs ? 0 : 1] : c.errGeneric);
      } else {
        routeUser(d.user);
      }
    } catch { setErr(c.errGeneric); }
    setBusy(false);
  };

  const doLogout = async () => {
    await clientFetch('/auth/client/logout', { method: 'POST' });
    setUser(null);
    setState('login');
  };

  if (state === 'checking') {
    return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center text-neutral-400 text-sm" data-testid="account-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  if (state === 'login') {
    const inputCls = 'w-full h-11 rounded-xl border border-neutral-300 px-4 text-[14px] outline-none focus:border-[#F77F11]';
    return (
      <AuthShell
        testId="account-login"
        image="/auth/client.png"
        eyebrow={isEs ? 'Mi Kitxens · Gratis' : 'My Kitxens · Free'}
        title={isEs ? 'El panel diario del restaurantero inteligente.' : 'The smart restaurateur’s daily dashboard.'}
        sub={isEs ? 'Noticias de tu zona, clima, competencia cercana, oportunidades de negocio y tu Index AI Score™ — gratis, para siempre.' : 'Local news, weather, nearby competitors, business opportunities and your Index AI Score™ — free, forever.'}
        links={[
          [
            { q: isEs ? '¿Eres partner?' : 'Are you a partner?', href: '/affiliates/dashboard', label: isEs ? 'Tu dashboard →' : 'Go to your dashboard →' },
            { q: isEs ? '¿Equipo Kitxens?' : 'Kitxens team?', href: '/login', label: isEs ? 'Accede al AI OS →' : 'Go to the AI OS →' },
          ],
          [
            { q: isEs ? '¿Quieres una demo?' : 'Want a demo?', href: '/demo', label: isEs ? 'Agéndala gratis →' : 'Book it free →' },
            { q: isEs ? '¿Aún no eres parte de Kitxens?' : 'Not part of Kitxens yet?', href: '/affiliates', label: isEs ? 'Únete a la comunidad →' : 'Join the community →' },
          ],
        ]}
      >
        <SEO path="/account" title={`${c.title} | Kitxens`} noindex />
        <div className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm">
          <div className="flex items-center gap-2.5">
            <img src="/kitxens-logo.png" alt="Kitxens" className="w-9 h-9 rounded-lg" />
            <p className="flex items-center gap-2 text-[#F77F11] text-[10px] tracking-[0.24em] uppercase font-bold"><UserCircle2 className="w-3.5 h-3.5" /> Mi Kitxens</p>
          </div>
          <h1 className="mt-4 text-xl font-bold text-neutral-900">{c.loginT}</h1>
          <p className="mt-1 text-[13.5px] text-neutral-500 leading-relaxed">{c.loginSub}</p>

          <button type="button" onClick={() => googleLoginRedirect('/account')}
            className="mt-5 w-full h-11 rounded-xl border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-800 font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2.5"
            data-testid="account-google-btn">
            <GoogleIcon /> {c.google}
          </button>

          <div className="my-5 flex items-center gap-3">
            <span className="flex-1 h-px bg-neutral-200" />
            <span className="text-[11px] text-neutral-400 font-semibold">{c.or}</span>
            <span className="flex-1 h-px bg-neutral-200" />
          </div>

          <div className="grid grid-cols-2 rounded-xl bg-neutral-100 p-1 text-[13px] font-bold">
            <button type="button" onClick={() => { setMode('login'); setErr(''); }}
              className={`h-9 rounded-lg transition-colors ${mode === 'login' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500'}`} data-testid="account-tab-login">
              {c.tabLogin}
            </button>
            <button type="button" onClick={() => { setMode('register'); setErr(''); }}
              className={`h-9 rounded-lg transition-colors ${mode === 'register' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500'}`} data-testid="account-tab-register">
              {c.tabRegister}
            </button>
          </div>

          <form onSubmit={submit} className="mt-4 space-y-3">
            {mode === 'register' && (
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={c.name} className={inputCls} data-testid="account-name-input" />
            )}
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={c.email} className={inputCls} data-testid="account-email-input" />
            <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={c.password} className={inputCls} data-testid="account-password-input" />
            {err && <p className="text-[12.5px] text-red-500" data-testid="account-login-error">{err}</p>}
            {mode === 'register' && <HumanCheck onToken={setHumanToken} />}
            <button type="submit" disabled={busy}
              className="w-full h-11 rounded-xl bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white font-bold text-[14px] transition-colors inline-flex items-center justify-center gap-2"
              data-testid="account-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {mode === 'register' ? c.submitRegister : c.submitLogin}
            </button>
          </form>
          <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-center" data-testid="account-restaurant-os-link">
            <p className="text-[12px] text-neutral-500">
              {isEs ? '¿Tu restaurante ya tiene un ' : 'Does your restaurant already have a '}<b className="text-neutral-800">Restaurant AI OS</b>?
            </p>
            <a href="/restaurant/login" className="text-[12.5px] font-bold text-[#F77F11] hover:underline">
              {isEs ? 'Accede a tu instancia privada →' : 'Access your private instance →'}
            </a>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (state === 'onboarding') {
    return (
      <div className="min-h-screen bg-[#F3F4F6] pt-32 md:pt-36 pb-20 px-4 flex flex-col items-center" data-testid="account-onboarding">
        <SEO path="/account" title={`${c.title} | Kitxens`} noindex />
        <ClientOnboarding isEs={isEs} initial={user || {}} onSaved={(u) => { setUser(u); setState('open'); }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen os3-scope bg-[#F3F4F6] pt-28 md:pt-32 pb-20" data-testid="account-page">
      <SEO path="/account" title={`${c.title} | Kitxens`} noindex />
      <div className="fixed top-20 right-6 z-30 flex items-center gap-2" data-testid="account-theme-toggle-wrap">
        <TextSizePill isEs={isEs} compact />
        <ThemeToggle theme={theme} setTheme={setTheme} isEs={isEs} />
      </div>
      <ClientDashboard user={user} isEs={isEs} onLogout={doLogout} onEditProfile={() => setState('onboarding')} />
    </div>
  );
}
